/**
 * Super Admin Dashboard
 * 
 * Features:
 * - View all companies
 * - Create company with admin + CID upload
 * - License upload and data extraction
 * - Subscription management
 * - Company statistics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from 'antd';
import {
  Card,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Tag,
  message,
  Input,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import superAdminService, {
  Company,
  CompanyStats,
  LicenseUploadResponse,
} from '../services/superAdminService';
import { useResponsive } from '../hooks/useResponsive';
import CreateCompanyModal from '../components/superadmin/CreateCompanyModal';
import SubscriptionModal from '../components/superadmin/SubscriptionModal';
import CompanyDetailsModal from '../components/superadmin/CompanyDetailsModal';
import EditCompanyModal from '../components/superadmin/EditCompanyModal';
import SuperAdminSidebar from '../components/superadmin/SuperAdminSidebar';
import SuperAdminHeader from '../components/superadmin/SuperAdminHeader';
import DashboardStatsCards from '../components/superadmin/DashboardStatsCards';
import GrowthTrendsChart from '../components/superadmin/GrowthTrendsChart';
import SubscriptionTrendsChart from '../components/superadmin/SubscriptionTrendsChart';
import UserActivityChart from '../components/superadmin/UserActivityChart';
import CompanyTypeDistribution from '../components/superadmin/CompanyTypeDistribution';
import OwnershipDistribution from '../components/superadmin/OwnershipDistribution';
import RecentRegistrations from '../components/superadmin/RecentRegistrations';

const { Text } = Typography;
const { Search } = Input;
const { Content } = Layout;

const SuperAdminDashboard: React.FC = () => {
  const { isMobile } = useResponsive();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future filter UI
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [licenseData, setLicenseData] = useState<LicenseUploadResponse | null>(null);
  
  // Modal states
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Load companies
  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await superAdminService.getCompanies({
        search: searchText || undefined,
        active_only: activeFilter,
      });
      setCompanies(data);
    } catch (error: any) {
      message.error(error.message || 'Failed to load companies');
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  }, [searchText, activeFilter]);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const data = await superAdminService.getAnalytics(6);
      setAnalytics(data);
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      // Don't show error message for analytics, just log it
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
    loadAnalytics();
  }, [loadCompanies, loadAnalytics]);

  // Auto-refresh analytics every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadAnalytics();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [loadAnalytics]);

  // Load company stats
  const loadCompanyStats = async (companyId: number) => {
    try {
      const stats = await superAdminService.getCompanyStats(companyId);
      setCompanyStats(stats);
    } catch (error: any) {
      message.error('Failed to load company statistics');
      console.error('Error loading stats:', error);
    }
  };

  // Handle create company
  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    loadCompanies();
    message.success('Company created successfully!');
  };


  // Handle view company details
  const handleView = async (company: Company) => {
    setSelectedCompany(company);
    await loadCompanyStats(company.id);
    setDetailsModalVisible(true);
  };

  // Handle edit company
  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedCompany(null);
    loadCompanies();
    message.success('Company updated successfully!');
  };

  // Handle delete company
  const handleDelete = async (companyId: number) => {
    try {
      await superAdminService.deleteCompany(companyId);
      message.success('Company deleted successfully');
      loadCompanies();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete company');
    }
  };

  // Handle subscription update
  const handleSubscription = (company: Company) => {
    setSelectedCompany(company);
    setSubscriptionModalVisible(true);
  };

  const handleSubscriptionSuccess = () => {
    setSubscriptionModalVisible(false);
    loadCompanies();
    message.success('Subscription updated successfully!');
  };

  // Calculate overall statistics
  const [, setOverallStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    totalLocations: 0,
  });

  // Load overall statistics (optimized with parallel loading)
  useEffect(() => {
    const loadOverallStats = async () => {
      if (companies.length === 0) {
        setOverallStats({
          totalCompanies: 0,
          activeCompanies: 0,
          totalUsers: 0,
          totalLocations: 0,
        });
        return;
      }

      try {
        // Load stats for all companies in parallel
        const statsPromises = companies.map(company =>
          superAdminService.getCompanyStats(company.id).catch(error => {
            console.error(`Error loading stats for company ${company.id}:`, error);
            return { total_users: 0, total_locations: 0 };
          })
        );
        
        const statsResults = await Promise.all(statsPromises);
        
        const totalUsers = statsResults.reduce((sum, stats) => sum + (stats.total_users || 0), 0);
        const totalLocations = statsResults.reduce((sum, stats) => sum + (stats.total_locations || 0), 0);
        
        setOverallStats({
          totalCompanies: companies.length,
          activeCompanies: companies.filter(c => c.is_active).length,
          totalUsers,
          totalLocations,
        });
      } catch (error) {
        console.error('Error loading overall stats:', error);
        // Set basic stats even if detailed stats fail
        setOverallStats({
          totalCompanies: companies.length,
          activeCompanies: companies.filter(c => c.is_active).length,
          totalUsers: 0,
          totalLocations: 0,
        });
      }
    };

    loadOverallStats();
  }, [companies]);

  // Table columns (reserved for future table view)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const columns: import('antd/es/table').ColumnsType<Company> = [
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Code: {record.code}
          </Text>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 120,
      render: (country) => country || '-',
    },
    {
      title: 'Plan',
      dataIndex: 'plan_type',
      key: 'plan_type',
      width: 100,
      render: (plan) => (
        <Tag color={plan === 'premium' ? 'gold' : plan === 'enterprise' ? 'purple' : 'blue'}>
          {plan?.toUpperCase() || 'BASIC'}
        </Tag>
      ),
    },
    {
      title: 'License Type',
      dataIndex: 'license_type',
      key: 'license_type',
      width: 120,
      render: (licenseType) => {
        if (!licenseType) return <Text type="secondary">-</Text>;
        return (
          <Tag color={licenseType === 'macro' ? 'cyan' : 'geekblue'}>
            {licenseType.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Limits',
      key: 'limits',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px' }}>
            Users: {record.max_users}
          </Text>
          <Text style={{ fontSize: '12px' }}>
            Locations: {record.max_locations}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Subscription',
      key: 'subscription',
      width: 180,
      render: (_, record) => {
        const expiresAt = record.subscription_expires_at;
        if (!expiresAt) {
          return <Tag color="green">No Expiration</Tag>;
        }
        const expiryDate = new Date(expiresAt);
        const now = new Date();
        const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        const isExpired = daysRemaining < 0;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={isExpired ? 'red' : daysRemaining < 30 ? 'orange' : 'green'}>
              {isExpired ? 'Expired' : `${daysRemaining} days left`}
            </Tag>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {expiryDate.toLocaleDateString()}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Manage Subscription">
            <Button
              type="link"
              icon={<CalendarOutlined />}
              onClick={() => handleSubscription(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete this company? This will delete the company and all its users."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleMenuClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <Layout className={`superadmin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ minHeight: '100dvh', background: '#f0f2f5', display: 'flex', flexDirection: 'row', overflowX: 'hidden', maxWidth: '100vw' }}>
      <SuperAdminHeader 
        onMenuClick={handleMenuClick}
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
      />
      <SuperAdminSidebar 
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        onCreateCompany={handleCreate}
        user={{
          name: 'Super Admin',
          email: 'admin@system.bt'
        }}
      />
      <div
        className="superadmin-content-wrapper"
        style={{
          flex: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? 80 : 280),
          marginTop: isMobile ? 64 : 80,
          transition: 'margin-left 0.3s ease, width 0.3s ease',
          width: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          maxWidth: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          minHeight: isMobile ? 'calc(100dvh - 64px)' : 'calc(100dvh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
      <Content 
        className="main-content-full superadmin-content"
        style={{ 
          flex: 1,
          minWidth: 0,
          margin: 0,
          padding: isMobile ? 12 : 24,
          background: '#ffffff',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <div style={{ width: '100%' }}>
          {/* Search Section */}
          <Card style={{ marginBottom: 24, borderRadius: 8, overflow: 'hidden' }}>
            <Search
              className="app-search"
              placeholder="Search companies..."
              allowClear
              enterButton={<Button type="primary" icon={<SearchOutlined />}>Search</Button>}
              size="large"
              style={{ width: isMobile ? '100%' : 400, maxWidth: '100%' }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => {
                setSearchText(value);
                loadCompanies();
              }}
            />
          </Card>

          {/* Statistics Cards */}
          <DashboardStatsCards overview={analytics?.overview} loading={analyticsLoading} />

          {/* Line Charts Row */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <GrowthTrendsChart data={analytics?.company_growth} loading={analyticsLoading} />
            </Col>
            <Col xs={24} lg={12}>
              <SubscriptionTrendsChart data={analytics?.subscription_trends} loading={analyticsLoading} />
            </Col>
          </Row>

          {/* User Activity Chart - Full Width */}
          <UserActivityChart data={analytics?.user_activity} loading={analyticsLoading} />

          {/* Charts Row */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={12}>
              <CompanyTypeDistribution data={analytics?.license_type_distribution} loading={analyticsLoading} />
            </Col>
            <Col xs={24} lg={12}>
              <OwnershipDistribution />
            </Col>
          </Row>

          {/* Recent Registrations */}
          <RecentRegistrations data={analytics?.recent_registrations} loading={analyticsLoading} />

          {/* Modals */}
      <CreateCompanyModal
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          setLicenseData(null);
        }}
        onSuccess={() => {
          handleCreateSuccess();
          setLicenseData(null);
        }}
        licenseData={licenseData}
      />

      <SubscriptionModal
        visible={subscriptionModalVisible}
        company={selectedCompany}
        onCancel={() => setSubscriptionModalVisible(false)}
        onSuccess={handleSubscriptionSuccess}
      />

      <CompanyDetailsModal
        visible={detailsModalVisible}
        company={selectedCompany}
        stats={companyStats}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedCompany(null);
          setCompanyStats(null);
        }}
      />

      <EditCompanyModal
        visible={editModalVisible}
        company={selectedCompany}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedCompany(null);
        }}
        onSuccess={handleEditSuccess}
      />
        </div>
      </Content>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;
